import { Location } from "@angular/common";
import { Component, OnDestroy } from "@angular/core";
import { INftDetails } from "../nft.interface";
import { OpenSeaAPIService } from "src/app/services/open-sea-api.service";
import { API_Const, OPEN_SEA_APIS } from "src/app/shared/constants/apiRouteConst";
import { ActivatedRoute } from "@angular/router";
import { EventService } from "src/app/services/event.service";
import { PopupService } from "src/app/services/popup.service";
import { CommonApiService } from "src/app/services/common-api-service";
import { CHAINS, CURRENCY, CURRENCY_SYMBOL, convertNumToCurr } from "src/app/shared/constants/app-enums";
import { GameAPIService } from "src/app/services/game-api.service";
import { DomSanitizer, SafeHtml } from "@angular/platform-browser";
import { Subscription } from "rxjs";

@Component({
  selector: "app-nft-details",
  templateUrl: "./nft-details.component.html",
  styleUrls: ["./nft-details.component.scss"],
})
export class NftDetailsComponent implements OnDestroy {
  tabIds = {
    USD: "USD",
    CHAIN: "CHAIN",
  };
  activeTabId = this.tabIds.USD;
  isSeeMoreExpanded!: boolean;
  nftDetails!: INftDetails;
  prizeId!: number;
  isUSDDefault = false;
  CURRENCY = CURRENCY;
  CURRENCY_SYMBOL = CURRENCY_SYMBOL;
  isLoading = true;
  fetchingNFTDetails = true;
  subscriptions: Subscription[] = [];

  constructor(
    private location: Location,
    private openSeaService: OpenSeaAPIService,
    private activatedRoute: ActivatedRoute,
    private eventService: EventService,
    private popupService: PopupService,
    private apiService: CommonApiService,
    private gameAPIService: GameAPIService,
    private sanitizer: DomSanitizer
  ) {
    this.eventService.showSpinner();
    this.nftDetails = {
      id: 3883,
      name: "",
      imgSrc: "assets/images/svg-icons/placeholder_view.svg",
      openSeaLink:
        "https://opensea.io/assets/blast/0x203e95fcc0561859b0c1ce09290fb41574992f12/1509",
      value: {
        current: 1200,
        max: 2400,
        min: 600,
      },
      valueInToken: {
        current: 12,
        max: 24,
        min: 6,
        symbol: "WSM",
        tokenPriceInUsd: 1234,
      },
      description: "",
      traits: [],
    };

    this.subscriptions.push(this.activatedRoute.params.subscribe((params) => {
      const { collectionName, nftId } = params;
      this.nftDetails.id = nftId;
      this.nftDetails.collection = collectionName;
      this.getCollectionDetails();
    }));

    this.subscriptions.push(this.activatedRoute.queryParams.subscribe({
      next: (queryParams) => {
        const { prizeId } = queryParams;
        this.prizeId = prizeId;
        this.getNftRangeData();
      }
    }));
    this.isUSDDefault = this.gameAPIService.userCurrency === CURRENCY.USD;
  }

  changeTab(event: any) {
    this.activeTabId = event.id;
  }

  back() {
    this.location.back();
  }

  /**
   * Gets Collection details from openSea
   */
  getCollectionDetails() {
    this.subscriptions.push(this.openSeaService
      .getDetail(OPEN_SEA_APIS.collection(this.nftDetails.collection as string))
      .subscribe({
        next: (res: any) => {
          const { collection, name, description, contracts, payment_tokens } =
            res;
          this.nftDetails.collection = collection;
          this.nftDetails.description = description;
          this.nftDetails.formattedDescription = this.sanitizeDescription(description);
          this.nftDetails.name = name;
          this.isLoading = false;
        },
        error: (err) => {
          this.isLoading = false;
          this.fetchingNFTDetails = false;
          this.popupService.messsageAlert("error", err.error.errors[0]);
          this.eventService.hideSpinner();
        }
      }));
  }

  getDisplayDescription(): SafeHtml {
    const completeDescription = this.nftDetails.description;
    if (this.isSeeMoreExpanded || completeDescription.length <= 300) {
      return this.sanitizeDescription(completeDescription);
    } else {
      const trimmedText = completeDescription.substring(0, 300) + '...';
      const formattedText = this.formatLinks(trimmedText);
      return this.sanitizer.bypassSecurityTrustHtml(formattedText);
    }
  }

  sanitizeDescription(description: string): SafeHtml {
    const formattedText = this.formatLinks(description);
    return this.sanitizer.bypassSecurityTrustHtml(formattedText);
  }

  formatLinks(text: string): string {
    return text.replace(/\[([^\]]+)\]\((https?:\/\/[^\s)]+)\)/g, '<a href="$2" target="_blank">$1</a>');
  }

  /**
   * Fetched NFT Details from openSea
   */
  getNftDetailsFromOpenSea() {
    this.subscriptions.push(this.openSeaService
      .getDetail(
        OPEN_SEA_APIS.nft(
          this.nftDetails.chain as string,
          this.nftDetails.address as string,
          this.nftDetails.id
        )
      )
      .subscribe({
        next: (res: any) => {
          const { nft } = res;
          const { opensea_url, traits = [], display_image_url, image_url
          } = nft;
          this.nftDetails.imgSrc = display_image_url ?? image_url;
          this.nftDetails.openSeaLink = opensea_url;
          traits?.forEach((trait: any) => {
            this.nftDetails.traits.push({
              type: trait.trait_type,
              value: this.processNFTTrait(trait.display_type, trait.value),
            });
          });
          this.eventService.hideSpinner();
          this.fetchingNFTDetails = false;
        },
        error: (err) => {
          this.popupService.messsageAlert("error", err.error.errors[0]);
          this.eventService.hideSpinner();
          this.fetchingNFTDetails = false;
        }
      }));
  }

  processNFTTrait(traitType: string, traitValue: any): string {
    switch (traitType) {
      case "date":
        return new Date(traitValue * 1000).toLocaleDateString();
      case "number":
        return `${traitValue}`;
      default:
        return traitValue;
    }
  }

  // getNFTListingDetails() {
  //   this.openSeaService
  //     .getDetail(
  //       OPEN_SEA_APIS.listing(
  //         this.nftDetails.collection as string,
  //         this.nftDetails.id
  //       )
  //     )
  //     .subscribe({
  //       next: (res: any) => {
  //         if (!Object.keys(res).length) {
  //           this.eventService.hideSpinner();
  //           return;
  //         }
  //         const {
  //           price: {
  //             current: { currency, decimals, value },
  //           },
  //         } = res;
  //         this.eventService.hideSpinner();
  //         const tokenCurrency = this.tokenDetails.filter(
  //           (token: any) => token.symbol === currency
  //         );
  //         const currentPrice = value / Math.pow(10, decimals);
  //         this.nftDetails.valueInToken = {
  //           current: currentPrice,
  //           symbol: currency,
  //           max: 100,
  //           min: 1,
  //           tokenPriceInUsd: tokenCurrency[0].usd_price,
  //         };
  //         this.nftDetails.value = {
  //           current: currentPrice * tokenCurrency[0].usd_price,
  //           max: 10000 + currentPrice * tokenCurrency[0].usd_price,
  //           min: 1000,
  //         };
  //       },
  //       error: (err) => {
  //         this.popupService.messsageAlert("error", err.error.errors[0]);
  //         this.eventService.hideSpinner();
  //       }
  //     });
  // }

  /**
   * Fetched range data from BE
   */
  getNftRangeData() {
    this.subscriptions.push(this.apiService.get(API_Const.lootboxes.getNftDetails(this.prizeId)).subscribe({
      next: (res) => {
        const { result: { nft_price_usd, nft_price_euro, nft_upper_limit_usd, nft_upper_limit_euro, nft_lower_limit_usd, nft_lower_limit_euro, nft_contract_name, nft_chain, nft_link } } = res;
        this.nftDetails.value = {
          current: this.isUSDDefault ? nft_price_usd : nft_price_euro,
          max: this.isUSDDefault ? nft_upper_limit_usd : nft_upper_limit_euro,
          min: this.isUSDDefault ? nft_lower_limit_usd : nft_lower_limit_euro,
        }
        this.nftDetails.address =
          nft_contract_name;
        this.nftDetails.chain = CHAINS[nft_chain]
        this.nftDetails.openSeaLink = nft_link;
        this.getNftDetailsFromOpenSea();
        this.eventService.hideSpinner();
      },
      error: (err) => {
        this.popupService.messsageAlert("error", err.error.message);
        this.eventService.hideSpinner();
      }
    }))
  }

  convertNumToCurr(num: number) {
    return convertNumToCurr(num);
  }

  ngOnDestroy(): void {
    this.subscriptions.forEach((subscription: Subscription) => subscription.unsubscribe())
  }

}
